import React from 'react'
import {Box, Flex, Stack} from '../../vanilla'
import {observer} from 'mobx-react-lite'
import {Container, Text} from '../../vanilla'
import {useContentStore, useGlobalStore} from '../../store/hooks/useStore'
import {Link} from '../link'
import {FacebookIcon, InstagramIcon, TwitterIcon, YoutubeIcon} from '../../components/icons/index'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {DefaultContentBody} from 'dc-delivery-sdk-js'
import {CollapsableMenu} from '../collapsable-menu'
import {APPLE_APP_STORE_LINK, GOOGLE_PLAY_MARKET_LINK} from '../../utils/constants'
import LazyLoad from 'react-lazyload'
import {AvailablePaymentMethodsIcons} from '../credit-card-type-icon'
import { FooterLink } from './footer-link'
import { ErrorBoundary } from '../error-boundary';
import { FooterLinkContent } from '../../types/cms/footer'

export interface FooterNavProps {
  children?: React.ReactNode
  title: string
  titleColor: string
  uniqueIdentifier: string
}

const FooterNav = ({title, titleColor, uniqueIdentifier, children}: FooterNavProps) => {
  return (
    <Stack
      width="full"
      paddingY="12px"
      paddingX="8px"
      borderBottom={['1px', '0px']}
      borderColor={'gray200'}
      style={{flexBasis: '20%'}}
      key={uniqueIdentifier}
    >
      <Box>
        <Text
          variant="text7"
          paddingY="2px"
          lineHeight="tall"
          style={{
            color: titleColor || '#000',
          }}
          data-test-selector={`footer-section-title-${title
            ?.toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')}`}
        >
          {title}
        </Text>
      </Box>
      <Stack>{children}</Stack>
    </Stack>
  )
}

const MediaContent = () => {
  return (
    <Flex direction={'row'} gap="16px">
      <Box
        as="a"
        href="https://www.facebook.com/icelandfoods"
        aria-label="Facebook"
        data-test-selector="footer-facebook-link"
      >
        <FacebookIcon style={{color: ' #878787', width: '26px', height: '26px'}} />
      </Box>
      <Box
        as="a"
        href="https://twitter.com/IcelandFoods"
        aria-label="Twitter"
        data-test-selector="footer-twitter-link"
      >
        <TwitterIcon style={{color: ' #878787', width: '26px', height: '26px'}} />
      </Box>
      <Box
        as="a"
        href="https://www.youtube.com/user/IcelandFoods?feature=watch"
        aria-label="Youtube"
        data-test-selector="footer-youtube-link"
      >
        <YoutubeIcon style={{color: ' #878787', width: '26px', height: '26px'}} />
      </Box>
      <Box
        as="a"
        href="https://www.instagram.com/icelandfoods/"
        aria-label="Instagram"
        data-test-selector="footer-instagram-link"
      >
        <InstagramIcon style={{color: ' #878787', width: '26px', height: '26px'}} />
      </Box>
    </Flex>
  )
}

const CardOptions = () => {
  return (
    <Stack spacing="12px" direction={['row', 'column']} justifyContent={'center'}>
      <Box as="a" href={APPLE_APP_STORE_LINK} data-test-selector="footer-app-store-link">
        <img
          alt="App Store"
          src={getAssetUrl('static/img/apple-app-store.svg')}
          loading="lazy"
          style={{
            display: 'block',
            width: '125px',
            height: '40px',
            objectFit: 'contain',
          }}
        />
      </Box>
      <Box as="a" href={GOOGLE_PLAY_MARKET_LINK} data-test-selector="footer-google-play-link">
        <img
          alt="Google Play"
          src={getAssetUrl('static/img/google-play.svg')}
          loading="lazy"
          style={{
            display: 'block',
            width: '125px',
            height: '36px',
            objectFit: 'contain',
          }}
        />
      </Box>
    </Stack>
  )
}

const Footer = observer(() => {
  const cms = useContentStore()
  const {availablePaymentMethods} = useGlobalStore()
  const nav = cms.contentById['footer-links'] as DefaultContentBody

  try{
    return (
      <ErrorBoundary renderErrorMessage={true}>
        <Box as="footer" bg="white" width="full" data-test-selector="footer">
          <Container paddingX={['4px', '8px', '16px']}>
            <Flex paddingY="20px" flexDirection={['column', 'row']}>
              {nav && nav.children.length && (
                <>
                {nav.children.map((titleNode: DefaultContentBody) => {
                  const {navDisplayName, showInMenu, menuTextColor} = titleNode.content.menuDefinition
                  return (
                    showInMenu && (
                      <Stack
                        width="full"
                        paddingY="12px"
                        paddingX="8px"
                        borderBottom={['1px', '0px']}
                        borderColor={'gray200'}
                        style={{flexBasis: '20%'}}
                        key={titleNode.content._meta.deliveryId}
                      >
                        {titleNode.children.length > 0 ? ( 
                          <>
                            <Box display={['contents', 'none']}>
                              <CollapsableMenu title={navDisplayName} titleColor={menuTextColor?.color}>
                                {titleNode.children.length > 0 ? (
                                  <Stack marginTop="12px" spacing="16px">
                                    {titleNode.children.map((linkNode: FooterLinkContent) => {
                                      return linkNode.content.menuDefinition.showInMenu ? (
                                        <FooterLink linkNode={linkNode}/>
                                      ) : null
                                    })}
                                  </Stack>
                                ) : null}
                              </CollapsableMenu>
                            </Box>

                            <Box display={['none', 'contents']}>
                              <FooterNav
                                title={navDisplayName}
                                titleColor={menuTextColor?.color}
                                uniqueIdentifier={titleNode.content._meta.deliveryId}
                              >
                                  <Stack spacing="16px">
                                    {titleNode.children.map((linkNode: FooterLinkContent) => {
                                        return linkNode.content.menuDefinition.showInMenu ? (
                                          <FooterLink linkNode={linkNode} />
                                        ) : null
                                    })}
                                  </Stack>
                              </FooterNav>
                            </Box>
                          </>
                          ) : null
                        }
                      </Stack>
                    )
                  )
                }
                )}
                </>
              )}

              <Stack
                width="full"
                paddingY="12px"
                paddingX="8px"
                borderBottom={['1px', '0px']}
                borderColor={'gray200'}
                style={{flexBasis: '20%'}}
              >
                <Box display={['contents', 'none']}>
                  <CollapsableMenu
                    title={'Stay Updated'}
                    titleColor={nav?.children[0]?.content.menuDefinition?.menuTextColor?.color}
                    dataTestSelector="footer-section-title-stay-updated"
                  >
                    <MediaContent />
                  </CollapsableMenu>
                </Box>
                <Box display={['none', 'contents']}>
                  <FooterNav
                    title={'Stay Updated'}
                    titleColor={nav?.children[0]?.content.menuDefinition?.menuTextColor?.color}
                    uniqueIdentifier={'Stay Updated'}
                  >
                    <MediaContent />
                  </FooterNav>
                </Box>
              </Stack>
              <Stack
                width="full"
                paddingY="12px"
                paddingX="8px"
                borderBottom={['1px', '0px']}
                borderColor={'gray200'}
                style={{flexBasis: '20%'}}
              >
                <Box display={['contents', 'none']}>
                  <CollapsableMenu
                    title={'Iceland Bonus Card App'}
                    titleColor={nav?.children[0]?.content.menuDefinition?.menuTextColor?.color}
                    dataTestSelector="footer-section-title-iceland-bonus-card-app">
                    <CardOptions />
                  </CollapsableMenu>
                </Box>
                <Box display={['none', 'contents']}>
                  <FooterNav
                    title={'Iceland Bonus Card App'}
                    titleColor={nav?.children[0]?.content.menuDefinition?.menuTextColor?.color}
                    uniqueIdentifier={'Iceland Bonus Card App'}
                  >
                    <CardOptions />
                  </FooterNav>
                </Box>
              </Stack>
            </Flex>

            <Flex
              paddingY="20px"
              paddingX={'8px'}
              borderTop={['0px', '1px']}
              borderColor={'gray200'}
              alignItems={'center'}
              justifyContent={['center', 'flex-end']}
              flexWrap={'wrap'}
              gap={'36px'}
            >
              <Stack
                direction={['column', 'row']}
                alignItems={'center'}
                gap={'8px'}
                data-test-selector="footer-payment-methods"
              >
                <Text
                  variant="text7"
                  paddingY="2px"
                  lineHeight="tall"
                  style={{color: nav?.children[0]?.content.menuDefinition?.menuTextColor?.color || '#000'}}
                >
                  We Securely Accept
                </Text>
                <LazyLoad>
                  <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    flexWrap="wrap"
                    gap={['8px', '16px']}
                  >
                    <AvailablePaymentMethodsIcons
                      availablePaymentMethods={availablePaymentMethods}
                      isFooter={true}
                    />
                  </Flex>
                </LazyLoad>
              </Stack>
            </Flex>
            <Stack
              paddingY="20px"
              paddingX={'8px'}
              alignItems={['flex-start', 'center']}
              justifyContent={'space-between'}
              flexWrap={'wrap'}
              gap={'36px'}
              flexDirection={['column-reverse', 'row']}
            >
              <Text variant="text3" color={'gray700'} data-test-selector="footer-copyright">
                © Copyright {new Date().getFullYear()} Iceland Foods Ltd.
              </Text>
              <Stack
                gap={'16px'}
                direction={['column', 'row']}
                alignItems={['flex-start', 'center']}
              >
                <Text
                  variant="text3"
                  as={Link}
                  to="/pages/sitemap"
                  color={'gray800'}
                  data-test-selector="footer-sitemap-link"
                >
                  Sitemap
                </Text>
                <Text
                  variant="text3"
                  as="a"
                  href="/terms.html"
                  color={'gray800'}
                  data-test-selector="footer-terms-link"
                >
                  Terms & Conditions
                </Text>
                <Text
                  variant="text3"
                  as="a"
                  href="/privacy-policy"
                  color={'gray800'}
                  data-test-selector="footer-privacy-link"
                >
                  Privacy
                </Text>
                <Text
                  variant="text3"
                  as="a"
                  href="/privacy-policy/cookie-policy"
                  color={'gray800'}
                  data-test-selector="footer-cookies-link"
                >
                  Cookies
                </Text>
                <Text
                  id="ot-sdk-btn"
                  className="ot-sdk-show-settings"
                  variant="text3"
                  as="a"
                  href="#"
                  color={'gray800'}
                  data-test-selector="footer-manage-cookies-link"
                >
                  Manage Cookies
                </Text>
              </Stack>
            </Stack>
          </Container>
        </Box>
      </ErrorBoundary>
    )
  } catch (error) {
    console.error(error)
    return <div></div>
  }
})

export default Footer
